import { CaretRight } from '@phosphor-icons/react';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { sensX, t } from '../../i18n';

/**
 * Petites briques d'interface reutilisees d'un ecran a l'autre :
 * en-tetes de section, pastilles, onglets, etats vides.
 */

/** Titre de section avec, a droite, un lien "Voir tout". */
export function SectionTitle({
  children,
  icon,
  action,
  to,
}: {
  children: ReactNode;
  icon?: ReactNode;
  /** Texte du lien de droite, "Voir tout" par defaut. */
  action?: string;
  to?: string;
}) {
  return (
    <div className="section-title">
      <h2>
        {icon}
        {children}
      </h2>
      {action && to && (
        <Link to={to} className="section-title__action">
          {action}
          <CaretRight className="sens-x" size={16} weight="bold" />
        </Link>
      )}
      {action && !to && <span className="section-title__note">{action}</span>}
    </div>
  );
}

/**
 * Selecteur a deux ou trois onglets, sur fond pastel.
 * "A venir / Passes", "Ce mois / Depuis toujours", "Inscrit / Organise".
 *
 * Le curseur est une case a part, posee en absolu, plutot qu'un fond
 * peint sur l'option choisie : une couleur de fond ne se deplace pas
 * d'un bouton a l'autre, elle s'allume ici et s'eteint la. Les options
 * se partageant la largeur en parts egales, un decalage d'une largeur de
 * curseur par rang suffit.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
}) {
  // Une valeur inconnue laisse le curseur sur la premiere option plutot
  // que de le sortir du cadre.
  const rang = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  return (
    <div
      className="segmented"
      role="tablist"
      style={{ '--parts': options.length } as CSSProperties}
    >
      {/*
        Le decalage est pose ici en clair plutot que derive d'une variable
        dans la feuille : une propriete personnalisee n'a pas de type, et
        WebKit a deja refuse d'interpoler chez nous ce que Chrome
        interpolait. Une valeur litterale ne laisse pas ce choix au moteur.
      */}
      <span
        className="segmented__glider"
        aria-hidden="true"
        style={{ transform: `translateX(${rang * 100 * sensX}%)` }}
      />

      {options.map((option) => (
        <button
          key={option.value}
          role="tab"
          aria-selected={option.value === value}
          className={`segmented__item${option.value === value ? ' segmented__item--on' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Sous-onglets d'une page : "Ce mois / Depuis toujours", "Inscrit /
 * Organise / Passes", "A venir / J'organise".
 *
 * Rien d'autre qu'un mot souligne. La pastille glissante sur fond pastel
 * disait la meme chose en prenant trois fois plus de place, et elle
 * ressemblait a un interrupteur — or ce ne sont pas deux reglages, ce
 * sont deux pages. Le trait sous le mot est la convention pour cela.
 *
 * Le trait est un element a part, pose en absolu et deplace, plutot
 * qu'une bordure allumee sur l'onglet choisi : une bordure ne se deplace
 * pas d'un onglet a l'autre, elle apparait ici et disparait la.
 */
export function Tabs<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
}) {
  // Une valeur inconnue laisse le trait sous la premiere option plutot
  // que de le sortir du cadre.
  const rang = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  return (
    <div className="tabs" role="tablist" style={{ '--parts': options.length } as CSSProperties}>
      {options.map((option) => (
        <button
          key={option.value}
          role="tab"
          aria-selected={option.value === value}
          className={`tabs__item${option.value === value ? ' tabs__item--on' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
      {/*
        Le decalage est pose en clair plutot que derive d'une variable
        dans la feuille : une propriete personnalisee n'a pas de type, et
        WebKit a deja refuse d'interpoler chez nous ce que Chrome
        interpolait. Une valeur litterale ne laisse pas ce choix au moteur.
      */}
      <span
        className="tabs__trait"
        aria-hidden="true"
        style={{ transform: `translateX(${rang * 100 * sensX}%)` }}
      />
    </div>
  );
}

/**
 * La coche, a droite d'un pseudo.
 *
 * C'est desormais la seule chose qui se pose la : le medaillon de niveau
 * que portaient les autres membres a ete retire. La coche pleine dit ce
 * qu'il faut — c'est bien lui — sans rien promettre d'autre.
 *
 * Elle sert deux choses qui se ressemblent a l'oeil et pas du tout dans
 * le code : le compte de l'application, et un compte verifie. Seul le mot
 * qu'elle porte les distingue, et il faut qu'il soit juste : « officiel »
 * sur le compte d'une personne serait un mensonge.
 */
export function BadgeOfficiel({
  size = 'md',
  application = false,
}: {
  size?: 'sm' | 'md';
  application?: boolean;
}) {
  const cote = size === 'sm' ? 21 : 26;
  const dit = application ? t.composants.badge.officiel : t.composants.badge.verifie;
  return (
    <span
      className="badge-officiel"
      style={{ width: cote, height: cote }}
      role="img"
      aria-label={dit}
      title={dit}
    >
      <svg viewBox="0 0 24 24" width={cote} height={cote} aria-hidden="true">
        <circle cx="12" cy="12" r="11" fill="currentColor" />
        <path
          d="M7.4 12.3l3.1 3.1 6.1-6.5"
          fill="none"
          stroke="#fff"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

/**
 * L'emoji pose a cote d'un pseudo (`PublicUser.badge`), la ou le pseudo
 * s'affiche : profil, suggestions d'amis, conversations. Rien s'il n'y en
 * a pas. Il se place dans le meme bloc que le nom, juste apres lui.
 */
export function BadgeEmoji({ user }: { user: { badge?: string } | null | undefined }) {
  if (!user?.badge) return null;
  return (
    <span className="drapeau badge-emoji" aria-hidden="true">
      {user.badge}
    </span>
  );
}

/** Etiquette de categorie d'evenement, coloree selon la categorie. */
export function CategoryTag({ label, color }: { label: string; color: string }) {
  return (
    <span className="category-tag" style={{ color }}>
      {label}
    </span>
  );
}

/**
 * Message affiche quand une liste est vide.
 *
 * Un vide sans issue est un cul-de-sac : il constate, et laisse le membre
 * devant rien. Quand quelque chose peut etre fait — ecrire le premier
 * post, creer la premiere sortie, se presenter — le bouton le dit et le
 * fait, plutot que de decrire ou aller le chercher.
 */
export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="empty-state">
      {icon}
      <p className="empty-state__title">{title}</p>
      {hint && <p className="empty-state__hint">{hint}</p>}
      {action && (
        <button type="button" className="empty-state__action" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}

/** Bandeau d'encouragement en bas d'un ecran ("Encore 50 pts pour..."). */
export function HintBanner({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="hint-banner">
      {icon}
      <p>{children}</p>
    </div>
  );
}

/** Barre de progression d'un trophee ou d'une destination. */
export function ProgressBar({ value, max, color }: { value: number; max: number; color?: string }) {
  const ratio = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
    >
      <span
        className="progress__fill"
        style={{ width: `${ratio * 100}%`, background: color ?? 'var(--s-primary)' }}
      />
    </div>
  );
}

/** Ligne cliquable des ecrans de reglages. */
export function SettingRow({
  icon,
  label,
  value,
  to,
  onClick,
  trailing,
  description,
}: {
  icon?: ReactNode;
  label: string;
  /** Valeur grise a droite, avant le chevron. */
  value?: string;
  to?: string;
  onClick?: () => void;
  /** Remplace le chevron : un interrupteur, un bouton radio. */
  trailing?: ReactNode;
  description?: string;
}) {
  const content = (
    <>
      {icon && <span className="setting-row__icon">{icon}</span>}
      <span className="setting-row__text">
        <span className="setting-row__label">{label}</span>
        {description && <span className="setting-row__description">{description}</span>}
      </span>
      {value && <span className="setting-row__value">{value}</span>}
      {trailing ??
        (to || onClick ? (
          <CaretRight size={17} weight="bold" className="sens-x setting-row__caret" />
        ) : null)}
    </>
  );

  if (to) {
    return (
      <Link to={to} className="setting-row">
        {content}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button className="setting-row" onClick={onClick}>
        {content}
      </button>
    );
  }
  return <div className="setting-row">{content}</div>;
}

/** Regroupe des lignes de reglages dans une carte a separateurs. */
export function SettingGroup({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="setting-group">
      {title && <h3 className="setting-group__title">{title}</h3>}
      <div className="setting-group__card">{children}</div>
    </section>
  );
}
